import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

/**
 * Wolt-style floating puck for the attractions scene (Android `DraggableAttractionsButton`).
 *
 * It is **not** an entry point the user meets on a fresh menu: the caller only renders it once the
 * scroll-revealed banner has been dismissed, so it reads as "the thing I put away", not as an
 * unexplained floating icon. It carries the pinned attraction's own photo (`imageUrl` — the same
 * image the banner leads with) and pulses gently, because by then it is the only remaining cue that
 * the feature exists.
 *
 * Two ways in:
 *  - **Tap** → `tap`, straight to the attractions list.
 *  - **Drag into the bottom 20%** → the caller crossfades this button out and the banner in the moment
 *    the line is crossed (finger still down), and reverses if the user drags back up. Releasing inside
 *    the zone fires `dropInZone` to commit.
 * Dragged anywhere else, it snaps to the nearest horizontal edge and its position persists.
 *
 * This is the only drag gesture in the app — built on plain pointer events + `setPointerCapture`. The
 * web port is simpler than the Compose original: there is no `pointerInput` re-keying to fight, so
 * `faded` (which flips mid-drag) is a pure `[style.opacity]` binding and never interrupts the gesture.
 * The single bridge back to the venue theme: the button uses `--menu-accent` ("I came from this
 * restaurant"), matching Android keeping `menuAccent` here.
 *
 * **Three elements, one transform each.** `.attr-fab-pos` owns the translate that positions the puck;
 * `.attr-fab` owns the pulse scale; the halo is a pseudo-element with its own scale. They cannot be
 * merged: a CSS `@keyframes` animating `transform` applies in the animation origin, which outranks
 * the style attribute — put the pulse on the positioned element and the running keyframe would
 * replace `[style.transform]` and snap the puck to the viewport's top-left. The halo is on the
 * positioner rather than inside `.attr-fab`, which is `overflow: hidden` for the photo and would
 * clip it.
 */
@Component({
    selector: 'app-attractions-fab',
    template: `
    <!-- The bottom drop zone, made visible while the puck is in flight. Without it the gesture is
         undiscoverable: the zone is invisible until the user happens to cross the line and the
         banner appears unannounced. Its top edge IS the line that opens the banner — which is why it
         is sized from the same measured H the zone test uses, not from 20vh — and it deepens once
         the puck is inside, the only "you are there" feedback the gesture has.
         pointer-events: none throughout: a hit-test candidate under the finger would steal the drag.
         The floating cart passing under it is intentional, so the bottom recedes to the target. -->
    <div class="attr-drop-scrim" [class.visible]="dragging" [class.in-zone]="overZone"
         [style.height.px]="dropZoneHeight" aria-hidden="true"></div>

    <!-- …and the place to let go. Drawn above the scrim but below the banner and the puck. -->
    <div class="attr-drop-target" [class.visible]="dragging" [class.in-zone]="overZone"
         aria-hidden="true">
      <app-attr-icon name="chevron-down" [size]="28"></app-attr-icon>
    </div>

    <div class="attr-fab-pos" [class.snapping]="snapping" [class.dragging]="dragging"
         [style.transform]="transform" [style.opacity]="faded ? 0 : 1"
         (pointerdown)="onPointerDown($event)" (pointermove)="onPointerMove($event)"
         (pointerup)="onPointerUp($event)" (pointercancel)="onPointerCancel($event)">
      <div class="attr-fab">
        <!-- Branch here rather than handing a null URL to app-attraction-image: it has no
             "blank means render nothing" mode and would show ImageBroken.svg, where Android falls
             back to the accent-filled icon (its generic empty-state artwork looks broken at 56px). -->
        <app-attraction-image *ngIf="imageUrl; else fabIcon" class="attr-fab-photo"
                              [url]="imageUrl" [alt]="'attractions.buttonCd' | translate">
        </app-attraction-image>
        <ng-template #fabIcon>
          <app-attr-icon name="explore" [size]="28"></app-attr-icon>
        </ng-template>
      </div>
    </div>
  `,
    styleUrls: ['./attractions-fab.component.scss'],
    standalone: false
})
export class AttractionsFabComponent implements OnInit, OnDestroy, OnChanges {
  /** Draws the button at opacity 0. Flips mid-drag when the zone line is crossed (still held). */
  @Input() faded = false;
  /** Accepts touches. False once committed (banner shown). */
  @Input() enabled = true;
  /** The pinned attraction's photo, filling the puck. Blank/null falls back to the explore icon. */
  @Input() imageUrl: string | null = null;

  /**
   * True from the moment the gesture passes the slop until release. Suspends the pulse and the halo:
   * a puck throbbing under the user's own finger reads as a glitch.
   *
   * (Android suspends only the pulse and lets the halo keep expanding — `DraggableAttractionsButton`
   * gates `pulse` on `isDragging` but reads the halo values unconditionally. Deliberate divergence:
   * once the magnet engages, a ring pulsing out from under the finger is noise.)
   */
  dragging = false;

  /** True while the puck's centre is below the zone line. Drives the scrim depth and the target. */
  overZone = false;

  @Output() tap = new EventEmitter<void>();
  @Output() dropInZone = new EventEmitter<void>();
  @Output() dragStateChanged = new EventEmitter<{ dragging: boolean; overZone: boolean }>();

  offsetX = 0;
  offsetY = 0;
  /** Toggles the CSS transform transition — off while dragging (instant follow), on for the settle. */
  snapping = false;

  private static readonly SIZE = 56;
  private static readonly MARGIN = 12;
  private static readonly DROP_ZONE = 0.2;
  private static readonly SLOP = 6;
  private static readonly CROSSFADE_MS = 220;
  /**
   * Distance from the viewport's bottom edge to the drop target's **centre**.
   *
   * The target's diameter lives only in the SCSS (it is purely visual), but this number is shared:
   * the stylesheet positions the circle at `bottom: 32px` so its centre lands here, and the magnet
   * below pulls toward the same point. Change one and the puck stops landing concentric.
   *
   * Deliberately a plain constant and never `env(safe-area-inset-bottom)`, which TS cannot read
   * anyway: the magnet maths and the drawn target have to agree, and 64 clears both a home indicator
   * and a 3-button bar.
   */
  private static readonly DROP_TARGET_BOTTOM_INSET = 64;
  /** How far the puck is pulled toward the target's centre while it is over the zone. */
  private static readonly MAGNET_PULL = 0.35;
  private static readonly DEFAULT_X = 1.0;
  private static readonly DEFAULT_Y = 0.72;
  private static readonly X_KEY = 'attractions_btn_x_fraction';
  private static readonly Y_KEY = 'attractions_btn_y_fraction';

  private W = 0;
  private H = 0;

  private pointerId: number | null = null;
  private startPointerX = 0;
  private startPointerY = 0;
  private startOffsetX = 0;
  private startOffsetY = 0;
  private moved = false;
  private homeResetTimer: ReturnType<typeof setTimeout> | null = null;
  /** 0 at rest, MAGNET_PULL while over the zone. Affects only what is drawn — never the offsets. */
  private magnet = 0;
  private magnetRaf: number | null = null;

  /**
   * What is DRAWN: the raw finger position, biased toward the drop target by `magnet`.
   *
   * `offsetX`/`offsetY` themselves stay exactly what the finger dictates, so `isInDropZone()` and
   * the drag-back-out reversal keep working off unmodified coordinates. Magnetising the stored
   * offsets instead would let the puck cross the zone line on its own and make the gesture
   * irreversible.
   *
   * Note the lerp targets the puck's TOP-LEFT (`targetTopLeft`, the target centre minus half the
   * puck), because that is what `translate` positions — lerping toward the target's centre would
   * land the puck half its size below and right of the ring it is supposed to sit inside.
   */
  get transform(): string {
    if (this.magnet === 0) {
      return `translate(${this.offsetX}px, ${this.offsetY}px)`;
    }
    const target = this.targetTopLeft();
    const x = this.offsetX + (target.x - this.offsetX) * this.magnet;
    const y = this.offsetY + (target.y - this.offsetY) * this.magnet;
    return `translate(${x}px, ${y}px)`;
  }

  /**
   * Height of the scrim: the bottom 20% of the same viewport height `isInDropZone` tests against, so
   * the gradient's top edge is literally the line that opens the banner. Sizing it in `20vh` instead
   * would drift from the test the moment a mobile URL bar collapses.
   */
  get dropZoneHeight(): number {
    return this.H * AttractionsFabComponent.DROP_ZONE;
  }

  ngOnInit(): void {
    this.measure();
    const home = this.savedOffset();
    this.offsetX = home.x;
    this.offsetY = home.y;
    window.addEventListener('resize', this.onResize);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.homeResetTimer) clearTimeout(this.homeResetTimer);
    if (this.magnetRaf !== null) cancelAnimationFrame(this.magnetRaf);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Once committed and invisible, move home unseen — so dismissing the banner brings the button
    // back where the user left it rather than stranding it inside the trigger zone. Keyed on
    // `enabled` (NOT `faded`): faded flips mid-drag while the finger is still down.
    if (changes['enabled'] && !changes['enabled'].firstChange && !this.enabled) {
      // Always clear the drag flags first, even though onPointerUp has normally done it: a gesture
      // that ended on an unusual path would otherwise leave the scrim and drop target stranded on
      // screen and the magnet engaged into the next drag.
      this.dragging = false;
      this.setOverZone(false);
      if (this.homeResetTimer) clearTimeout(this.homeResetTimer);
      this.homeResetTimer = setTimeout(() => {
        const home = this.savedOffset();
        this.offsetX = home.x;
        this.offsetY = home.y;
      }, AttractionsFabComponent.CROSSFADE_MS + 20);
    }
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.enabled) return;
    (event.target as Element).setPointerCapture(event.pointerId);
    this.pointerId = event.pointerId;
    this.startPointerX = event.clientX;
    this.startPointerY = event.clientY;
    this.startOffsetX = this.offsetX;
    this.startOffsetY = this.offsetY;
    this.moved = false;
    this.snapping = false; // follow the finger instantly
  }

  onPointerMove(event: PointerEvent): void {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startPointerX;
    const dy = event.clientY - this.startPointerY;

    if (!this.moved && Math.hypot(dx, dy) > AttractionsFabComponent.SLOP) {
      this.moved = true;
      this.dragging = true;
      this.setOverZone(this.isInDropZone(this.offsetY));
      this.dragStateChanged.emit({ dragging: true, overZone: this.overZone });
    }
    if (!this.moved) return;

    const size = AttractionsFabComponent.SIZE;
    const margin = AttractionsFabComponent.MARGIN;
    const maxY = Math.max(margin, this.H - size - margin);
    this.offsetX = this.clamp(this.startOffsetX + dx, 0, Math.max(0, this.W - size));
    this.offsetY = this.clamp(this.startOffsetY + dy, margin, maxY);
    this.setOverZone(this.isInDropZone(this.offsetY));
    this.dragStateChanged.emit({ dragging: true, overZone: this.overZone });
  }

  onPointerUp(event: PointerEvent): void {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;
    this.pointerId = null;

    // A stationary press is unambiguously a tap.
    if (!this.moved) {
      this.tap.emit();
      return;
    }

    this.dragging = false;
    this.setOverZone(false);
    this.dragStateChanged.emit({ dragging: false, overZone: false });

    if (this.isInDropZone(this.offsetY)) {
      // Open the banner. No snap and no persist: the caller fades the button out and it returns home.
      this.dropInZone.emit();
      return;
    }

    // Snap to the nearest horizontal edge and persist the settled fraction.
    const size = AttractionsFabComponent.SIZE;
    const margin = AttractionsFabComponent.MARGIN;
    const maxX = Math.max(0, this.W - size - margin);
    const maxY = Math.max(0, this.H - size - margin);
    const dockLeft = this.offsetX + size / 2 < this.W / 2;
    const targetX = dockLeft ? margin : maxX;
    const targetY = this.offsetY;

    this.snapping = true;
    this.offsetX = targetX;
    this.offsetY = targetY;

    if (maxX > 0 && maxY > 0) {
      this.persist(targetX / maxX, targetY / maxY);
    }
  }

  onPointerCancel(event: PointerEvent): void {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;
    this.pointerId = null;
    if (this.moved) {
      this.dragging = false;
      this.setOverZone(false);
      this.dragStateChanged.emit({ dragging: false, overZone: false });
    }
  }

  private setOverZone(value: boolean): void {
    if (this.overZone === value) return;
    this.overZone = value;
    this.tweenMagnet(value ? AttractionsFabComponent.MAGNET_PULL : 0);
  }

  /** Centre of the drop target, expressed as the puck's top-left so it can be lerped directly. */
  private targetTopLeft(): { x: number; y: number } {
    const half = AttractionsFabComponent.SIZE / 2;
    return {
      x: this.W / 2 - half,
      y: this.H - AttractionsFabComponent.DROP_TARGET_BOTTOM_INSET - half
    };
  }

  /**
   * Eases the magnet scalar so entering and leaving the zone glide rather than jump.
   *
   * Only the **scalar** is tweened, and the lerp above is recomputed from scratch every frame — the
   * one thing that must NOT happen is a CSS `transition` on the resulting transform. While the
   * finger is inside the zone the scalar is constant but the offsets are rewritten on every
   * `pointermove`, so a transition on the product would restart each frame and leave the puck
   * visibly trailing the finger.
   *
   * `requestAnimationFrame` is zone-patched, so each frame ticks change detection and the
   * `[style.transform]` binding re-reads `transform` without any manual `detectChanges`.
   */
  private tweenMagnet(to: number): void {
    if (this.magnetRaf !== null) {
      cancelAnimationFrame(this.magnetRaf);
      this.magnetRaf = null;
    }
    const from = this.magnet;
    if (from === to) return;

    const startedAt = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - startedAt) / AttractionsFabComponent.CROSSFADE_MS);
      this.magnet = from + (to - from) * AttractionsFabComponent.ease(p);
      if (p < 1) {
        this.magnetRaf = requestAnimationFrame(step);
      } else {
        this.magnet = to;
        this.magnetRaf = null;
      }
    };
    this.magnetRaf = requestAnimationFrame(step);
  }

  /**
   * Samples `cubic-bezier(0.4, 0, 0.2, 1)` — Compose's FastOutSlowIn, the same curve the scrim, the
   * drop target and the banner slide on. Newton-Raphson to invert x, then evaluate y.
   */
  private static ease(t: number): number {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const cx = 1.2, bx = -1.8, ax = 1.6;   // from p1x = 0.4, p2x = 0.2
    const cy = 0, by = 3, ay = -2;         // from p1y = 0.0, p2y = 1.0
    let x = t;
    for (let i = 0; i < 5; i++) {
      const fx = ((ax * x + bx) * x + cx) * x - t;
      const slope = (3 * ax * x + 2 * bx) * x + cx;
      if (Math.abs(slope) < 1e-6) break;
      x -= fx / slope;
    }
    return ((ay * x + by) * x + cy) * x;
  }

  private isInDropZone(offsetY: number): boolean {
    return this.H > 0 && offsetY + AttractionsFabComponent.SIZE / 2 > this.H * (1 - AttractionsFabComponent.DROP_ZONE);
  }

  private measure(): void {
    // The app column, not the window. On a phone `body` spans the viewport and this is
    // `innerWidth` (minus a scrollbar, which is the more correct number anyway); on desktop the app
    // is a centred fixed-width column and `.attr-fab-pos` is pinned to its left edge
    // (styles/desktop.scss), so the puck's whole x-space — the snap-to-edge, the drop target's
    // centre, the clamp — is the column's, and the puck can never dock out in the dark margin.
    this.W = document.body.clientWidth;
    this.H = window.innerHeight;
  }

  private onResize = (): void => {
    this.measure();
    // Re-seed from the persisted fraction on a size change (matches the Compose `remember(size)`).
    const home = this.savedOffset();
    this.offsetX = home.x;
    this.offsetY = home.y;
  };

  private savedOffset(): { x: number; y: number } {
    const size = AttractionsFabComponent.SIZE;
    const margin = AttractionsFabComponent.MARGIN;
    const fx = this.readFraction(AttractionsFabComponent.X_KEY, AttractionsFabComponent.DEFAULT_X);
    const fy = this.readFraction(AttractionsFabComponent.Y_KEY, AttractionsFabComponent.DEFAULT_Y);

    const maxX = Math.max(0, this.W - size - margin);
    const maxY = Math.max(0, this.H - size - margin);
    // Kept clear of the drop zone: a release inside the zone is never persisted, but a position saved
    // before this zone existed could be, and would park the button on its own trigger.
    //
    // This is also why there is no safe-area allowance in the clamp: it keeps the puck out of the
    // bottom 20% of the viewport, so at rest it is never anywhere near the home indicator, and the
    // 12px bottom margin is only reachable mid-drag, under the user's own finger.
    const restingMaxY = this.clamp(this.H * (1 - AttractionsFabComponent.DROP_ZONE) - size, margin, Math.max(margin, maxY));

    return {
      x: this.clamp(fx * maxX, margin, Math.max(margin, maxX)),
      y: this.clamp(fy * maxY, margin, restingMaxY)
    };
  }

  private persist(fx: number, fy: number): void {
    try {
      localStorage.setItem(AttractionsFabComponent.X_KEY, String(fx));
      localStorage.setItem(AttractionsFabComponent.Y_KEY, String(fy));
    } catch {
      // Storage may be unavailable (private mode); the position simply won't survive a reload.
    }
  }

  private readFraction(key: string, fallback: number): number {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      const value = parseFloat(raw);
      return Number.isFinite(value) ? value : fallback;
    } catch {
      return fallback;
    }
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
